import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authAPI } from '../lib/api';
import type { UserResponse } from '../types';

type UserRole = 'ADMIN' | 'DIRECTOR' | 'LEAD' | 'USER' | 'READ_ONLY';

interface AuthContextValue {
  user: UserResponse | null;
  login: (email: string, password: string) => Promise<UserResponse>;
  loginWithToken: (token: string | null, refreshToken?: string | null) => Promise<UserResponse>;
  logout: () => void;
  refreshUser: () => Promise<UserResponse | null>;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isDirector: boolean;
  isLead: boolean;
  isUser: boolean;
  isReadOnly: boolean;
  canEditTasks: boolean;
  /** K40 — true when ADMIN or when employee's department code starts with 'OPS'. */
  canEditInvoiced: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // #38 — PostHog now only fires for authenticated sessions. Pre-auth visitors
  // don't phone home. Idempotent guard inside initPostHog() means re-renders
  // here are safe.
  useEffect(() => {
    if (user && typeof window !== 'undefined' && !window.__posthogInited) {
      import('../lib/analytics').then((m) => m.initPostHog()).catch(() => {
        // analytics failures must never break the app
      });
    }
  }, [user]);

  const refreshUser = useCallback(async (): Promise<UserResponse | null> => {
    try {
      const response = await authAPI.me();
      const userData = response.data;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error: unknown) {
      console.error('Failed to refresh user data:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('user');

      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e: unknown) {
          console.error('Failed to parse stored user', e);
        }
      }

      // Always try to verify session via httpOnly cookie
      if (storedUser) {
        try {
          const response = await authAPI.me();
          const userData = response.data;
          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);
        } catch (error: unknown) {
          console.error('Session verification failed:', error);
          setUser(null);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string): Promise<UserResponse> => {
    const response = await authAPI.login(email, password);
    const { user: userData } = response.data;
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const loginWithToken = async (token: string | null, refreshToken: string | null = null): Promise<UserResponse> => {
    // httpOnly cookies are set by the backend; just verify the session
    const response = await authAPI.me();
    const userData = response.data;
    localStorage.setItem('user', JSON.stringify(userData));
    // Clear any stale localStorage tokens from previous auth system
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(userData);
    return userData;
  };

  const logout = () => {
    authAPI.logout().catch(() => {});
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const role = user?.role as UserRole | undefined;
  // K40: ADMIN can always edit; non-admins must have an Operations department
  // (case-insensitive prefix "OPS") so future Ops hires are auto-included.
  const departmentCode = String(user?.department_code || '').trim().toUpperCase();
  const value: AuthContextValue = {
    user,
    login,
    loginWithToken,
    logout,
    refreshUser,
    loading,
    isAuthenticated: !!user,
    isAdmin: role === 'ADMIN',
    isDirector: role === 'DIRECTOR',
    isLead: role === 'LEAD',
    isUser: role === 'USER',
    isReadOnly: role === 'READ_ONLY',
    canEditTasks: !!role && ['ADMIN', 'DIRECTOR', 'LEAD', 'USER'].includes(role),
    // K-Feedback-2026-06-21 #4 — DIRECTOR now mirrors ADMIN on Invoiced (Kim).
    canEditInvoiced: role === 'ADMIN' || role === 'DIRECTOR' || departmentCode.startsWith('OPS'),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
